import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { changelogService, jobService, jobStepService } from '../services';
import {
  Asset,
  formatData,
  formatDuration,
  handleError,
  Role,
  Operation,
  TaskType,
} from '../helpers';
import {
  Button,
  DataTable,
  Icon,
  MainColumn,
  ObjectDetails,
  SkeletonLoader,
  Tabs,
  PrincipalLink,
} from '../components';
import { ChangelogItemRow } from '../components/';
import {
  useChangelog,
  useInterval,
  useModalDialog,
  useGlobalState,
  useNotFound,
  useMessage,
  usePhotos,
  useQueryParams,
} from '../hooks';

export const Job = () => {
  const { queryParams, setHistory, resetParams } = useQueryParams();

  const [job, setJob] = useState({});
  const [jobSteps, setJobSteps] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [loading, setLoading] = useState(true);
  const [loadingJobSteps, setLoadingJobSteps] = useState(true);

  const [defaultSelectedObject, setDefaultSelectedObject] = useState({ tab: queryParams.object });
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });
  const [defaultSchemaChangesOnly, setDefaultSchemaChangesOnly] = useState(
    queryParams.schemaChangesOnly
  );

  const { job_id } = useParams();

  const { showMessage } = useMessage();
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { changelog, loadingChangelog, fetchChangelogByJobId } = useChangelog({ jobId: job_id });
  const { fetchPhotos } = usePhotos();
  const { notFound, setNotFound, NotFound } = useNotFound();

  const isAdmin = loggedInUser && loggedInUser.user_role_name === Role.admin;

  // Poll while queued or running so the UI transitions through all states automatically
  useInterval(() => {
    if (isAdmin) {
      if (job && (job.job_status === 'queued' || job.job_status === 'running')) {
        fetchItems();
      }
      if (
        job &&
        job.job_status === 'running' &&
        (defaultSelectedTab.tab === 'job_steps' || !defaultSelectedTab.tab)
      ) {
        fetchJobSteps();
      }
    }
  }, 3000);

  useEffect(() => {
    fetchItems(true);
    isAdmin && fetchJobSteps();

    if ((defaultSelectedTab.tab === 'changelog' || !isAdmin) && !changelog) {
      fetchChangelogByJobId(undefined, defaultSchemaChangesOnly);
    }
  }, []);

  useEffect(() => {
    changelog && selectObject();
  }, [changelog]);

  const fetchItems = async (fetchPhoto = false) => {
    jobService
      .getById(job_id)
      .then(res => {
        if (!res) return;
        if (res.jobs.length < 1) {
          setNotFound(true);
        } else {
          const job_tmp = res.jobs[0];
          setJob({
            ...job,
            ...job_tmp,
            connector_name_tooltip:
              job_tmp.connector_code === 'odbc'
                ? `${job_tmp.connector_name} (${job_tmp.connector_driver_name})`
                : job_tmp.connector_name,
          });

          setLoading(false);
          isAdmin && fetchPhoto && fetchPhotos(res.jobs[0], 'user_id', setJob);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      });
  };

  const fetchJobSteps = async () => {
    jobStepService
      .getByJobId(job_id)
      .then(res => {
        if (!res) return;
        let maxValue = 0;

        res.job_steps.forEach(row => {
          if (row.job_step_runtime_ms / 1000 > maxValue) {
            maxValue = row.job_step_runtime_ms / 1000;
          }
        });

        const tmp = res.job_steps.map(job_step => {
          return {
            ...job_step,
            job_step_runtime_seconds: job_step.job_step_runtime_ms / 1000,
            job_step_runtime_seconds_max_value: maxValue,
            job_step_runtime_tooltip:
              formatDuration(job_step.job_step_runtime_ms / 1000) + ' (hh:mm:ss)',
            job_step_affected_rows:
              job_step.job_step_affected_rows !== null ? job_step.job_step_affected_rows : '-',
            job_step_tooltip: job_step.job_step_message || job_step.job_step_status,
          };
        });

        setJobSteps(tmp);
        setLoadingJobSteps(false);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const selectObject = () => {
    let defaultObject;

    Object.entries(changelog).forEach(([key, value]) => {
      if (value.length > 0 && defaultObject === undefined) {
        defaultObject = key;
      }
    });

    setDefaultSelectedObject({ tab: defaultObject });
  };

  const handleShowMessage = (data, messageType = 'showMessage') => {
    showModalDialog(messageType, data);
  };

  const handleShowFilterOnly = ({ schema_changes_only }) => {
    fetchChangelogByJobId(undefined, schema_changes_only);
    setDefaultSchemaChangesOnly(schema_changes_only);
    setHistory({ schemaChangesOnly: schema_changes_only });
  };

  const onObjectChange = object => {
    resetParams({ object: object });
    setDefaultSelectedObject({ tab: object });
  };

  const onTabChange = tab => {
    resetParams({ tab: tab });

    if (!changelog) {
      fetchChangelogByJobId();
    }
  };

  const data = loading ? [] : job;

  const isRunning = data.job_status === 'running';

  const isNullableValues = {
    true: { label: undefined, className: '' },
    false: { label: 'NN', className: 'pill pill-grey pill-narrow', tooltip: 'Not Null' },
  };

  if (notFound || (!isAdmin && !loading && data.job_status !== 'completed')) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Job"
          title={formatData(data.job_enqueued_timestamp || data.job_started_timestamp, 'datetime')}
          subtitleIcon={<Icon name={data.job_status} tooltip={data.job_status} />}
          subtitleText={data && !loading && data.task_name + ' job ' + data.job_status}
          showDescription={false}
          description={data.job_completion_message}
          loading={loading}
          details={[
            {
              title: 'Runtime',
              data: data.job_runtime_seconds ? formatDuration(data.job_runtime_seconds) : null,
              tooltip: `${data.job_runtime_seconds} seconds`,
            },
            { title: 'Queued', data: formatData(data.job_enqueued_timestamp, 'datetime') },
            { title: 'Started', data: formatData(data.job_started_timestamp, 'datetime') },
            {
              title: 'Completed',
              data: data.job_completed_timestamp
                ? formatData(data.job_completed_timestamp, 'datetime')
                : null,
            },
            { title: 'Id', data: data.job_id, tooltip: 'Katalogue-internal id for this asset' },
          ]}
        >
          {isAdmin && (
            <div className="display-linebreak object-description">
              {!loading ? (
                data.job_status === 'failed' ? (
                  <Button
                    value="Show Error"
                    className="button"
                    onClick={() => handleShowMessage(data.job_completion_message)}
                  />
                ) : (
                  data.job_completion_message
                )
              ) : (
                <SkeletonLoader width="10vw" />
              )}
            </div>
          )}
          {data.task_type_code === TaskType.dssync && (
            <React.Fragment>
              <h3>{!loading ? 'Connection' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? (
                  data.connection_name === null ? (
                    '-'
                  ) : data.connection_id ? (
                    <Link to={'/connections/' + data.connection_id} className="link">
                      <Icon name={data.connector_code} tooltip={data.connector_name_tooltip} />
                      {data.connection_name}
                    </Link>
                  ) : (
                    <React.Fragment>
                      <Icon name={data.connector_code} tooltip={data.connector_name_tooltip} />
                      {data.connection_name}
                    </React.Fragment>
                  )
                ) : (
                  <SkeletonLoader width="10vw" />
                )}
              </div>
            </React.Fragment>
          )}

          {isAdmin && (
            <React.Fragment>
              <h3>{!loading ? 'Execution Method' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? data.job_execution_method : <SkeletonLoader width="10vw" />}
              </div>

              <h3>{!loading ? 'Executed By' : <SkeletonLoader width="5vw" />}</h3>
              <PrincipalLink
                userId={data.user_id}
                principalName={data.job_executed_by_user}
                principalPhoto={data.user_photo}
                principalType={Asset.User}
                userTitle={data.user_title}
                userDepartment={data.user_department}
                loading={loading}
              />
            </React.Fragment>
          )}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          onTabChange={onTabChange}
          disableTabsWithoutResults={true}
          showResultCount={false}
          showNoResultsMessage={false}
          defaultSelectedTab={defaultSelectedTab}
        >
          {isAdmin && (
            <div label="Job Steps" tabid="job_steps">
              {jobSteps.length > 0 || loadingJobSteps ? (
                <DataTable
                  columns={[
                    { id: 'job_step_status', type: 'icon', tooltip: 'job_step_tooltip' },
                    { id: 'job_step_execution_order', name: '' },
                    { id: 'job_step_definition_name', name: 'Job Step', className: 'bold' },
                    { id: 'job_step_started_timestamp', name: 'Started', type: 'datetime' },
                    { id: 'job_step_completed_timestamp', name: 'Completed', type: 'datetime' },
                    {
                      id: 'job_step_runtime_seconds',
                      name: 'Runtime [s]',
                      type: 'chart',
                      chartType: 'bar',
                      data: {
                        data: 'job_step_runtime_seconds',
                        max: 'job_step_runtime_seconds_max_value',
                      },
                      tooltip: 'job_step_runtime_tooltip',
                    },
                    {
                      id: 'job_step_affected_rows',
                      name: 'Affected Rows',
                      type: 'integer',
                      className: 'right',
                    },
                  ]}
                  data={jobSteps}
                  idColumn="job_step_id"
                  loading={loadingJobSteps}
                  editable={false}
                  filterable={true}
                  filterObjects={['job_step_status', 'job_step_definition_name']}
                />
              ) : (
                !loadingJobSteps && (
                  <div className="no-result-text">No job steps found for this task</div>
                )
              )}
            </div>
          )}
          <div label="Changelog" tabid="changelog" resultCount={job.changed_asset_count || 0}>
            <Tabs
              className="buttons left"
              onTabChange={onObjectChange}
              disableTabsWithoutResults={true}
              defaultSelectedTab={defaultSelectedObject}
              loading={loadingChangelog}
            >
              {data.task_type_code === 'dssync' && (
                <div
                  label="Dataset Groups"
                  tabid={Asset.DatasetGroup}
                  resultCount={(changelog && changelog?.dataset_group?.length) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'operation',
                        name: 'Operation',
                        value: 'operation',
                        values: Operation,
                        type: 'pill',
                        className: 'type-pill',
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Name',
                        tooltip: 'dataset_group_description',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                        className: 'bold',
                      },
                    ]}
                    data={changelog?.dataset_group || []}
                    idColumn="dataset_group_id"
                    loading={loadingChangelog}
                    editable={false}
                    expandable={true}
                    expandableComponents={{
                      dataKey: 'changedData',
                      showHeader: true,
                      Component: ChangelogItemRow,
                      columns: [
                        {
                          id: 'property',
                          name: 'Property',
                          value: 'property',
                          className: 'small-bold-text no-break width-150',
                        },
                        { id: 'new', name: 'To', className: 'width-150' },
                        { id: 'old', name: 'From', className: '' },
                      ],
                    }}
                    expandedRowClassName="background-white"
                    filterable={true}
                    filterObjects={['operation', 'dataset_group_name']}
                    filterObjectsValueMap={{
                      operation: Operation,
                    }}
                    filterControls={[
                      {
                        type: 'checkbox',
                        name: 'schema_changes_only',
                        label: 'Schema Changes Only',
                        tooltip: 'Show schema changes only',
                        onChange: handleShowFilterOnly,
                        defaultValue: defaultSchemaChangesOnly,
                      },
                    ]}
                  />
                </div>
              )}

              {data.task_type_code === 'dssync' && (
                <div
                  label="Datasets"
                  tabid={Asset.Dataset}
                  resultCount={(changelog && changelog?.dataset?.length) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'operation',
                        name: 'Operation',
                        value: 'operation',
                        values: Operation,
                        type: 'pill',
                        className: 'type-pill',
                      },
                      {
                        id: 'dataset_name',
                        name: 'Name',
                        tooltip: 'dataset_description',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id?tab=changelog',
                        className: 'bold',
                        inline: [{ type: 'icon', icon: 'dataset_type_name' }],
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Dataset Group',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                      },
                    ]}
                    data={changelog?.dataset || []}
                    idColumn="dataset_id"
                    loading={loadingChangelog}
                    editable={false}
                    expandable={true}
                    indentIcons={true}
                    expandableComponents={[
                      {
                        dataKey: 'fields',
                        showHeader: false,
                        className: 'condensed-left',
                        columns: [
                          {
                            id: 'operation',
                            name: 'Operation',
                            value: 'operation',
                            values: Operation,
                            type: 'pill',
                            className: 'type-pill',
                          },
                          {
                            id: 'datatype_category',
                            name: '',
                            type: 'icon',
                            tooltip: 'datatype_fullname',
                            className: 'no-right-margin',
                          },
                          {
                            id: 'field_name',
                            name: 'Name',
                            link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id?tab=changelog',
                          },
                          { id: 'datatype_fullname', name: 'Datatype', className: 'small-text' },
                          {
                            id: 'is_nullable',
                            name: '',
                            type: 'boolean',
                            values: isNullableValues,
                          },
                        ],
                      },
                      {
                        dataKey: 'changedData',
                        showHeader: true,
                        Component: ChangelogItemRow,
                        className: 'margin-10',
                        columns: [
                          {
                            id: 'property',
                            name: 'Property',
                            value: 'property',
                            className: 'small-bold-text no-break width-150',
                          },
                          { id: 'new', name: 'To', className: 'width-150' },
                          { id: 'old', name: 'From', className: '' },
                        ],
                      },
                    ]}
                    expandedRowClassName="background-white"
                    filterable={true}
                    filterObjects={[
                      'operation',
                      'dataset_name',
                      'dataset_group_name',
                      'dataset_type_name',
                      'fields.field_name',
                    ]}
                    filterObjectsValueMap={{
                      operation: Operation,
                    }}
                    filterControls={[
                      {
                        type: 'checkbox',
                        name: 'schema_changes_only',
                        label: 'Schema Changes Only',
                        tooltip: 'Show schema changes only',
                        onChange: handleShowFilterOnly,
                        defaultValue: defaultSchemaChangesOnly,
                      },
                    ]}
                  />
                </div>
              )}

              {data.task_type_code === 'dssync' && (
                <div
                  label="Fields"
                  tabid={Asset.Field}
                  resultCount={(changelog && changelog?.field?.length) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'operation',
                        name: 'Operation',
                        value: 'operation',
                        values: Operation,
                        type: 'pill',
                        className: 'type-pill',
                      },
                      {
                        id: 'field_name',
                        name: 'Name',
                        className: 'bold',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id?tab=changelog',
                        inline: [
                          { type: 'icon', icon: 'datatype_category', tooltip: 'datatype_fullname' },
                        ],
                      },
                      { id: 'datatype_fullname', name: 'Datatype', className: 'small-text' },
                      { id: 'is_nullable', name: '', type: 'boolean', values: isNullableValues },
                      {
                        id: 'dataset_type_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'dataset_type_name',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'dataset_name',
                        name: 'Dataset',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id',
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Dataset Group',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                      },
                    ]}
                    data={changelog?.field || []}
                    idColumn="field_id"
                    loading={loadingChangelog}
                    editable={false}
                    expandable={true}
                    expandableComponents={{
                      dataKey: 'changedData',
                      showHeader: true,
                      Component: ChangelogItemRow,
                      columns: [
                        {
                          id: 'property',
                          name: 'Property',
                          value: 'property',
                          className: 'small-bold-text no-break width-150',
                        },
                        { id: 'new', name: 'To', className: 'width-150' },
                        { id: 'old', name: 'From', className: '' },
                      ],
                    }}
                    expandedRowClassName="background-white"
                    filterable={true}
                    filterObjects={[
                      'operation',
                      'field_name',
                      'dataset_name',
                      'dataset_group_name',
                      'datatype_fullname',
                      'dataset_type_name',
                    ]}
                    filterObjectsValueMap={{
                      operation: Operation,
                    }}
                    filterControls={[
                      {
                        type: 'checkbox',
                        name: 'schema_changes_only',
                        label: 'Schema Changes Only',
                        tooltip: 'Show schema changes only',
                        onChange: handleShowFilterOnly,
                        defaultValue: defaultSchemaChangesOnly,
                      },
                    ]}
                  />
                </div>
              )}

              {data.task_type_code === 'dssync' && (
                <div
                  label="Attributes"
                  tabid={Asset.Attribute}
                  resultCount={(changelog && changelog?.attribute?.length) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'operation',
                        name: 'Operation',
                        value: 'operation',
                        values: Operation,
                        type: 'pill',
                        className: 'type-pill',
                      },
                      {
                        id: 'attribute_name',
                        name: 'Name',
                        tooltip: 'attribute_description',
                        className: 'bold',
                        link: '/attributes/:attribute_id?tab=changelog',
                        inline: [{ type: 'icon', icon: 'attribute_role_name' }],
                      },
                    ]}
                    data={changelog?.attribute || []}
                    idColumn="attribute_id"
                    loading={loadingChangelog}
                    editable={false}
                    expandable={true}
                    expandableComponents={{
                      dataKey: 'changedData',
                      showHeader: true,
                      Component: ChangelogItemRow,
                      columns: [
                        {
                          id: 'property',
                          name: 'Property',
                          value: 'property',
                          className: 'small-bold-text no-break width-150',
                        },
                        { id: 'new', name: 'To', className: 'width-150' },
                        { id: 'old', name: 'From', className: '' },
                      ],
                    }}
                    expandedRowClassName="background-white"
                    filterable={true}
                    filterObjects={['operation', 'attribute_role_name', 'attribute_name']}
                  />
                </div>
              )}

              {data.task_type_code === 'adsync' && (
                <div
                  label="User Groups"
                  tabid={Asset.UserGroup}
                  resultCount={(changelog && changelog?.user_group?.length) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'operation',
                        name: 'Operation',
                        value: 'operation',
                        values: Operation,
                        type: 'pill',
                        className: 'type-pill',
                      },
                      {
                        id: 'user_group_name',
                        name: '',
                        type: 'owner',
                        link: '/user_groups/:user_group_id',
                        data: { photo: 'user_group_photo' },
                      },
                      {
                        id: 'user_group_name',
                        name: 'Name',
                        tooltip: 'user_group_description',
                        link: '/user_groups/:user_group_id',
                        className: 'bold',
                      },
                    ]}
                    data={changelog?.user_group || []}
                    idColumn="user_group_name"
                    loading={loadingChangelog}
                    editable={false}
                    expandable={true}
                    expandableComponents={[
                      {
                        dataKey: 'users',
                        showHeader: false,
                        className: 'condensed-left',
                        columns: [
                          {
                            id: 'operation',
                            name: 'Operation',
                            value: 'operation',
                            values: Operation,
                            type: 'pill',
                            className: 'type-pill',
                          },
                          {
                            id: 'user_fullname',
                            name: '',
                            type: 'owner',
                            link: '/users/:user_id',
                            data: { photo: 'user_photo' },
                          },
                          {
                            id: 'user_fullname',
                            name: 'Name',
                            link: '/users/:user_id',
                            className: 'bold',
                          },
                          { id: 'user_username', name: 'Username', className: 'small-text' },
                        ],
                      },
                      {
                        dataKey: 'changedData',
                        showHeader: true,
                        Component: ChangelogItemRow,
                        columns: [
                          {
                            id: 'property',
                            name: 'Property',
                            value: 'property',
                            className: 'small-bold-text no-break width-150',
                          },
                          { id: 'new', name: 'To', className: 'width-150' },
                          { id: 'old', name: 'From', className: '' },
                        ],
                      },
                    ]}
                    expandedRowClassName="background-white"
                    filterable={true}
                    filterObjects={[
                      'operation',
                      'user_group_name',
                      'user_group_description',
                      'user_group_email',
                      'users.user_fullname',
                      'users.user_username',
                    ]}
                    filterObjectsValueMap={{
                      operation: Operation,
                    }}
                  />
                </div>
              )}

              {data.task_type_code === 'adsync' && (
                <div
                  label="Users"
                  tabid={Asset.User}
                  resultCount={(changelog && changelog?.user?.length) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'operation',
                        name: 'Operation',
                        value: 'operation',
                        values: Operation,
                        type: 'pill',
                        className: 'type-pill',
                      },
                      {
                        id: 'user_fullname',
                        name: '',
                        type: 'owner',
                        link: '/users/:user_id',
                        data: { photo: 'user_photo' },
                      },
                      {
                        id: 'user_fullname',
                        name: 'Name',
                        link: '/users/:user_id',
                        className: 'bold',
                      },
                      { id: 'user_username', name: 'Username', className: 'small-text' },
                    ]}
                    data={changelog?.user || []}
                    idColumn="user_fullname"
                    loading={loadingChangelog}
                    editable={false}
                    expandable={true}
                    expandableComponents={{
                      dataKey: 'changedData',
                      showHeader: true,
                      Component: ChangelogItemRow,
                      columns: [
                        {
                          id: 'property',
                          name: 'Property',
                          value: 'property',
                          className: 'small-bold-text no-break width-150',
                        },
                        { id: 'new', name: 'To', className: 'width-150' },
                        { id: 'old', name: 'From', className: '' },
                      ],
                    }}
                    expandedRowClassName="background-white"
                    filterable={true}
                    filterObjects={['operation', 'user_fullname', 'user_username', 'user_email']}
                    filterObjectsValueMap={{
                      operation: Operation,
                    }}
                  />
                </div>
              )}
            </Tabs>
          </div>
        </Tabs>
      </div>
    </div>
  );
};
